import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  Input,
  Select,
  Text,
  useToast,
  List,
  ListItem,
  Spinner,
  Collapse,
  Icon,
  FormControl,
} from "@chakra-ui/react";
import { useEffect, useState, useCallback, useRef } from "react";
import { IoPersonRemove } from "react-icons/io5";
import { IoMdAdd } from "react-icons/io";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { useFormik } from "formik";
import * as Yup from "yup";
import LabelInput from "../../../components/Input";
import InputAndDragAndDrop from "../../../components/InputWithDragAndDrop";
import { Link } from "react-router-dom";
import { fetchSuggestions } from "../../../actions/location.actions";
import states from "../../../constants/states.json";

// ─── Regex Patterns ───────────────────────────────────────────────────────────

const MOBILE_REGEX = /^[6-9]\d{9}$/;
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const NAME_REGEX = /^[A-Za-z\s&.'()\-]+$/;
const LETTERS_ONLY_REGEX = /^[A-Za-z\s,]+$/;
const EMAIL_REGEX = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9\-]+(\.[a-zA-Z0-9\-]+)*\.[a-zA-Z]{2,}$/;

// ─── Validation Schema ────────────────────────────────────────────────────────

const buildValidationSchema = () =>
  Yup.object({
    uniqueVendorCode: Yup.string().required("Vendor code is required"),

    vendorName: Yup.string()
      .required("Vendor name is required")
      .min(2, "Name must be at least 2 characters")
      .max(50, "Vendor name must not exceed 50 characters")
      .matches(NAME_REGEX, "Only letters, spaces and & . ' ( ) - are allowed — no numbers or special characters"),

    phoneNumber: Yup.string()
      .required("Mobile number is required")
      .matches(MOBILE_REGEX, "Enter a valid 10-digit Indian mobile number starting with 6-9"),

    whatsappNumber: Yup.string()
      .required("WhatsApp number is required")
      .matches(MOBILE_REGEX, "Enter a valid 10-digit Indian mobile number starting with 6-9"),

    vendorEmail: Yup.string()
      .required("Email is required")
      .email("Please enter a valid email address e.g. name@example.com"),

    vendorType: Yup.string()
      .required("Vendor type is required")
      .oneOf(["individual", "organisation"], "Please select a valid vendor type"),

    aadhaarNumberIndividual: Yup.string().when("vendorType", {
      is: "individual",
      then: (s) =>
        s.notRequired().test('aadhaar-validation', 'Aadhaar must be exactly 12 digits', function(value) {
          if (!value) return true;
          return /^\d{12}$/.test(value);
        }).optional(),
      otherwise: (s) => s.optional(),
    }),

    panNumberIndividual: Yup.string().when("vendorType", {
      is: "individual",
      then: (s) =>
        s.notRequired().test('pan-validation', 'Invalid PAN format. Use: ABCDE1234F', function(value) {
          if (!value) return true;
          return PAN_REGEX.test(value);
        }).optional(),
      otherwise: (s) => s.optional(),
    }),

    gstinIndividual: Yup.string().when("vendorType", {
      is: "individual",
      then: (s) => s.test('gstin-validation', 'Invalid GSTIN format. Use: 22ABCDE1234F1Z5', function(value) {
        if (!value) return true;
        return GSTIN_REGEX.test(value);
      }).optional(),
      otherwise: (s) => s.optional(),
    }),

    gstStateIndividual: Yup.string().optional(),
    tradeNameIndividual: Yup.string().optional(),

    companyName: Yup.string().when("vendorType", {
      is: "organisation",
      then: (s) =>
        s
          .required("Company name is required")
          .min(2, "Company name must be at least 2 characters")
          .max(50, "Company name must not exceed 50 characters")
          .matches(NAME_REGEX, "Only letters, spaces and & . ' ( ) - are allowed — no numbers"),
      otherwise: (s) => s.optional(),
    }),

    companyPanNumber: Yup.string().when("vendorType", {
      is: "organisation",
      then: (s) =>
        s.test('company-pan-validation', 'Invalid PAN format. Use: ABCDE1234F', function(value) {
          if (!value) return true;
          return PAN_REGEX.test(value);
        }).required("Company PAN is required"),
      otherwise: (s) => s.optional(),
    }),

    // ── Company Owner KYC — all optional ──
    ownerAadhaarNumber: Yup.string().when("vendorType", {
      is: "organisation",
      then: (s) =>
        s.notRequired().test('owner-aadhaar-validation', 'Aadhaar must be exactly 12 digits', function(value) {
          if (!value) return true;
          return /^\d{12}$/.test(value);
        }).optional(),
      otherwise: (s) => s.optional(),
    }),

    ownerPanNumber: Yup.string().when("vendorType", {
      is: "organisation",
      then: (s) =>
        s.notRequired().test('owner-pan-validation', 'Invalid PAN format. Use: ABCDE1234F', function(value) {
          if (!value) return true;
          return PAN_REGEX.test(value);
        }).optional(),
      otherwise: (s) => s.optional(),
    }),

    organisationAddress: Yup.string().when("vendorType", {
      is: "organisation",
      then: (s) =>
        s
          .required("Company address is required")
          .min(10, "Please enter a complete address (min 10 characters)")
          .max(50, "Address must not exceed 50 characters"),
      otherwise: (s) => s.optional(),
    }),

    organisationCity: Yup.string().when("vendorType", {
      is: "organisation",
      then: (s) =>
        s
          .required("City is required")
          .min(2, "City must be at least 2 characters")
          .max(50, "City name must not exceed 50 characters")
          .matches(LETTERS_ONLY_REGEX, "City should only contain letters and spaces"),
      otherwise: (s) => s.optional(),
    }),

    // organisationState: Yup.string().when("vendorType", {
    //   is: "organisation",
    //   then: (s) =>
    //     s.required("State is required").min(3, "State must be at least 3 characters").max(50, "State name must not exceed 50 characters"),
    //   otherwise: (s) => s.optional(),
    // }),

    organisationPincode: Yup.string().when("vendorType", {
      is: "organisation",
      then: (s) =>
        s.required("Pincode is required").matches(/^\d{6}$/, "Pincode must be exactly 6 digits"),
      otherwise: (s) => s.optional(),
    }),

    organisationCountry: Yup.string().when("vendorType", {
      is: "organisation",
      then: (s) =>
        s
          .required("Country is required")
          .min(2, "Country must be at least 2 characters")
          .max(50, "Country name must not exceed 50 characters")
          .matches(LETTERS_ONLY_REGEX, "Country should only contain letters and spaces"),
      otherwise: (s) => s.optional(),
    }),

    gstin: Yup.string().when("vendorType", {
      is: "organisation",
      then: (s) => s.test('org-gstin-validation', 'Invalid GSTIN format. Use: 22ABCDE1234F1Z5', function(value) {
        if (!value) return true;
        return GSTIN_REGEX.test(value);
      }).optional(),
      otherwise: (s) => s.optional(),
    }),

    gstState: Yup.string().optional(),
    tradeNameOrganisation: Yup.string().max(50, "trade org name must not exceed 50 characters").optional(),

    vendorContacts: Yup.array().when("vendorType", {
      is: "organisation",
      then: (s) =>
        s.of(
          Yup.object({
            name: Yup.string()
              .required("Contact name is required")
              .min(2, "Name must be at least 2 characters")
              .max(50, "Contact name must not exceed 50 characters")
              .matches(NAME_REGEX, "Only letters and spaces allowed — no numbers or special characters"),
            mobile: Yup.string()
              .required("Contact number is required")
              .matches(MOBILE_REGEX, "Enter a valid 10-digit mobile number starting with 6-9"),
            email: Yup.string()
              .required("Contact email is required")
              .matches(EMAIL_REGEX, "Enter a valid email address e.g. name@gmail.com"),
          })
        ),
      otherwise: (s) => s.optional(),
    }),

    additionalDetails: Yup.string().max(50, "Additional details must not exceed 50 characters").optional(),

    vendorStatus: Yup.string().required("Vendor status is required"),
  });

// ─── Inline Error Component ───────────────────────────────────────────────────

const FieldError = ({ error, touched, value }) => {
  // Don't show error if field hasn't been touched
  if (!touched) return null;
  // Don't show required error if field is empty and not yet validated
  if (!value && error === "Aadhaar number is required") return null;
  if (!value && error === "PAN number is required") return null;
  if (!value && error === "Company PAN is required") return null;
  if (!value && error === "Owner Aadhaar is required") return null;
  if (!value && error === "Owner PAN is required") return null;
  if (!value && error === "Company name is required") return null;
  if (!value && error === "Company address is required") return null;
  if (!value && error === "City is required") return null;
  if (!value && error === "State is required") return null;
  if (!value && error === "Pincode is required") return null;
  if (!value && error === "Country is required") return null;
  
  return error ? (
    <Text color="red.500" fontSize="xs" mt={1}>
      {error}
    </Text>
  ) : null;
};

// ─── Main Form Component ──────────────────────────────────────────────────────

const Form = ({ data, setData, handleSubmit, formRef }) => {
  const toast = useToast();
  const toastIdRef = useRef({});

  const [isSameAsMobile, setIsSameAsMobile] = useState(false);
  const [isGstSectionOpen, setIsGstSectionOpen] = useState(false);
  const [isOrgGstSectionOpen, setIsOrgGstSectionOpen] = useState(false);

  const [citySuggestions, setCitySuggestions] = useState([]);
  const [loadingCity, setLoadingCity] = useState(false);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [noOfFleet, setNoOfFleet] = useState("");

  const debounceTimerRef = useRef(null);

  const showToastOnce = (key, options) => {
    if (!toastIdRef.current[key] || !toast.isActive(toastIdRef.current[key])) {
      toastIdRef.current[key] = toast({ ...options });
    }
  };

  const generateUniqueVendorCode = () => {
    const randomPart = Math.random().toString(36).slice(2, 7).toUpperCase();
    return `VND-${randomPart}`;
  };

  const initValues = {
    uniqueVendorCode: data?.uniqueVendorCode || "",
    vendorName: data?.vendorName || "",
    phoneNumber: data?.contactDetails?.phoneNumber || data?.phoneNumber || "",
    whatsappNumber: data?.contactDetails?.whatsappNumber || data?.whatsappNumber || "",
    vendorEmail: data?.contactDetails?.email || data?.vendorEmail || "",
    vendorType: data?.kyc?.vendorType || data?.vendorType || "",
    aadhaarNumberIndividual: data?.kyc?.individual?.aadhaar?.documentNumber || data?.aadhaarNumberIndividual || "",
    panNumberIndividual: data?.kyc?.individual?.pancard?.documentNumber || data?.panNumberIndividual || "",
    tradeNameIndividual: data?.kyc?.individual?.gstDetails?.tradeName || data?.tradeNameIndividual || "",
    gstinIndividual: data?.kyc?.individual?.gstDetails?.gstin || data?.gstinIndividual || "",
    gstStateIndividual: data?.kyc?.individual?.gstDetails?.gstState || data?.gstStateIndividual || "",
    organisationType: data?.kyc?.organisation?.organisationType || data?.organisationType || "",
    companyName: data?.kyc?.organisation?.companyName || data?.companyName || "",
    companyPanNumber: data?.kyc?.organisation?.pancard?.documentNumber || data?.companyPanNumber || "",
    tradeNameOrganisation: data?.kyc?.organisation?.gstDetails?.tradeName || data?.tradeNameOrganisation || "",
    gstin: data?.kyc?.organisation?.gstDetails?.gstin || data?.gstin || "",
    gstState: data?.kyc?.organisation?.gstDetails?.gstState || data?.gstState || "",
    ownerAadhaarNumber: data?.kyc?.organisation?.aadhaar?.documentNumber || data?.ownerAadhaarNumber || "",
    ownerPanNumber: data?.kyc?.organisation?.ownerPancard?.documentNumber || data?.ownerPanNumber || "",
    organisationAddress: data?.kyc?.organisation?.orgAddress?.address || data?.orgAddress?.address || data?.organisationAddress || "",
    organisationCity: data?.kyc?.organisation?.orgAddress?.city || data?.orgAddress?.city || data?.organisationCity || "",
    organisationState: data?.kyc?.organisation?.orgAddress?.state || data?.orgAddress?.state || data?.organisationState || "",
    organisationPincode: data?.kyc?.organisation?.orgAddress?.pinCode || data?.orgAddress?.pinCode || data?.organisationPincode || "",
    organisationCountry: data?.kyc?.organisation?.orgAddress?.country || data?.orgAddress?.country || data?.organisationCountry || "",
    vendorRating: 1,
    vendorPrioritisation: 1,
    vendorStatus: data?.vendorStatus || "under_review",
    additionalDetails: data?.additionalDetails || "",
    aadhaarCardFront: data?.kyc?.individual?.aadhaar?.front || data?.aadhaarCardFront || "",
    aadhaarCardBack: data?.kyc?.individual?.aadhaar?.back || data?.aadhaarCardBack || "",
    panCardFront: data?.kyc?.individual?.pancard?.front || data?.panCardFront || "",
    gstCertificate: data?.kyc?.individual?.gstCertificate || data?.kyc?.organisation?.gstCertificate || data?.gstCertificate || "",
    companyPanCardFront: data?.kyc?.organisation?.pancard?.front || data?.companyPanCardFront || "",
    ownerPanCardFront: data?.kyc?.organisation?.ownerPancard?.front || data?.ownerPanCardFront || "",
    vendorContacts: data?.vendorContacts?.length ? data.vendorContacts : [],
  };

  const formik = useFormik({
    initialValues: initValues,
    validationSchema: buildValidationSchema(),
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        await handleSubmit(values);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { values, errors, touched, handleBlur, setFieldValue, setFieldTouched } = formik;

  // ─── Submit Handler ────────────────────────────────────────────────────────

const handleFormSubmit = async (e) => {
  e.preventDefault();

  const validationErrors = await formik.validateForm();

  // ✅ Only check REAL errors
  const hasErrors = Object.values(validationErrors).some(
    (val) => val !== undefined && val !== null && val !== ""
  );

  if (hasErrors) {
    formik.setTouched(
      Object.keys(validationErrors).reduce((acc, key) => {
        if (validationErrors[key]) {
          acc[key] = true;
        }
        return acc;
      }, {}),
      true
    );

    const getFirstMsg = (obj) => {
      if (!obj) return null;
      if (typeof obj === "string") return obj;

      if (Array.isArray(obj)) {
        for (const e of obj) {
          const m = getFirstMsg(e);
          if (m) return m;
        }
      }

      if (typeof obj === "object") {
        for (const key of Object.keys(obj)) {
          const m = getFirstMsg(obj[key]);
          if (m) return m;
        }
      }

      return null;
    };

    showToastOnce("submit-error", {
      title: "Please fix the errors before submitting",
      description:
        getFirstMsg(validationErrors) ||
        "Some fields are missing or invalid.",
      status: "error",
      duration: 4000,
      isClosable: true,
      position: "top-right",
    });

    return;
  }

  await formik.submitForm();
};

  // ─── Field Handlers ────────────────────────────────────────────────────────

  const handleChange = (e) => {
    const { name, value } = e.target;
    let v = value;

    if (["vendorName", "companyName"].includes(name)) {
      v = value.replace(/[^A-Za-z\s&.'()\-]/g, "").slice(0, 51);
    }

    if (["phoneNumber", "whatsappNumber"].includes(name)) {
      v = value.replace(/\D/g, "").slice(0, 10);
    }

    if (["aadhaarNumberIndividual", "ownerAadhaarNumber"].includes(name)) {
      v = value.replace(/\D/g, "").slice(0, 12);
    }

    if (["panNumberIndividual", "companyPanNumber", "ownerPanNumber"].includes(name)) {
      v = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10);
    }

    if (["gstin", "gstinIndividual"].includes(name)) {
      v = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 15);
    }

    if (name === "organisationPincode") {
      v = value.replace(/\D/g, "").slice(0, 6);
    }

    if (["organisationCity", "organisationCountry"].includes(name)) {
      v = value.replace(/[^A-Za-z\s]/g, "");
    }

    setFieldValue(name, v, true);
    setFieldTouched(name, true, true);
    
    // Force immediate validation
    setTimeout(() => {
      formik.validateField(name);
    }, 0);

    if (name === "phoneNumber" && isSameAsMobile) {
      setFieldValue("whatsappNumber", v, true);
      setTimeout(() => {
        formik.validateField("whatsappNumber");
      }, 0);
    }
  };

  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    setFieldValue(name, value, true);
    setFieldTouched(name, true, true);
    
    // Force immediate validation
    setTimeout(() => {
      formik.validateField(name);
    }, 0);
  };

  // ─── City Autocomplete ─────────────────────────────────────────────────────

  const handleCityChange = useCallback(
    (value) => {
      const clean = value.replace(/[^A-Za-z\s]/g, "");
      setFieldValue("organisationCity", clean, true);
      setFieldTouched("organisationCity", true, true);
      
      // Force immediate validation
      setTimeout(() => {
        formik.validateField("organisationCity");
      }, 0);

      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

      if (!clean.trim()) {
        setCitySuggestions([]);
        setShowCityDropdown(false);
        return;
      }

      debounceTimerRef.current = setTimeout(async () => {
        setLoadingCity(true);
        setShowCityDropdown(true);
        try {
          const response = await fetchSuggestions(clean, "cities");
          setCitySuggestions(response?.data || []);
        } catch (err) {
          console.error("City fetch error:", err);
        } finally {
          setLoadingCity(false);
        }
      }, 300);
    },
    [setFieldValue, setFieldTouched, formik]
  );

  const handleCitySelect = (location) => {
    setFieldValue("organisationCity", location.description || location.name, true);
    setShowCityDropdown(false);
    setCitySuggestions([]);
    
    // Force immediate validation
    setTimeout(() => {
      formik.validateField("organisationCity");
    }, 0);
  };

  // ─── Vendor Contacts ───────────────────────────────────────────────────────

  const handleAddNewContacts = () => {
    setFieldValue("vendorContacts", [
      ...values.vendorContacts,
      { name: "", email: "", mobile: "" },
    ]);
  };

  const handleRemoveContact = (index) => {
    setFieldValue("vendorContacts", values.vendorContacts.filter((_, i) => i !== index));
  };

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!values.uniqueVendorCode) {
      setFieldValue("uniqueVendorCode", generateUniqueVendorCode());
    }
  }, []);

  useEffect(() => {
    if (values.phoneNumber && values.whatsappNumber && values.phoneNumber === values.whatsappNumber) {
      setIsSameAsMobile(true);
    } else {
      setIsSameAsMobile(false);
    }
  }, [values.phoneNumber, values.whatsappNumber]);

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <Box>
      <form ref={formRef} onSubmit={handleFormSubmit}>

        {/* ── Step 1: Vendor Information ── */}
        <Box px={6} py={8} borderRadius="lg" bg="white" my="2rem">
          <Heading fontSize="lg" mb={4} color="#1A202C">Vendor Information</Heading>
          <Flex justifyContent="space-between" fontSize="0.875rem" letterSpacing={0} mb={6}>
            <Text color="#90A3BF">Please provide basic info</Text>
            <Text color="#90A3BF">Step 1 of 2</Text>
          </Flex>

          <Grid templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }} gap={4} mb={6}>
            {/* Unique Vendor Code */}
            <GridItem>
              <LabelInput
                placeholder="VND-XXXXX"
                label="Unique Vendor Code"
                name="uniqueVendorCode"
                isRequired
                value={values.uniqueVendorCode}
                onBlur={handleBlur}
                onChange={handleChange}
                isReadOnly
                bg={values.uniqueVendorCode ? "gray.100" : "white"}
              />
              <FieldError error={errors.uniqueVendorCode} touched={touched.uniqueVendorCode} value={values.uniqueVendorCode} />
            </GridItem>

            {/* Vendor Name */}
            <GridItem>
              <LabelInput
                placeholder="Enter name"
                label="Vendor Name"
                name="vendorName"
                isRequired
                value={values.vendorName}
                onBlur={handleBlur}
                onChange={handleChange}
                bg={values.vendorName ? "gray.100" : "white"}
              />
              <FieldError error={errors.vendorName} touched={touched.vendorName} value={values.vendorName} />
            </GridItem>

            {/* Phone Number */}
            <GridItem>
              <LabelInput
                placeholder="Enter Mobile Number"
                label="Mobile Number"
                name="phoneNumber"
                type="tel"
                isRequired
                value={values.phoneNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                inputMode="numeric"
                bg={values.phoneNumber ? "gray.100" : "white"}
              />
              <FieldError error={errors.phoneNumber} touched={touched.phoneNumber} value={values.phoneNumber} />
            </GridItem>

            {/* WhatsApp Number */}
            <GridItem>
              <Flex justify="space-between" align="center" mb={2}>
                <FormLabel fontSize="1rem" color="#1A202C" mb={0}>WhatsApp Number</FormLabel>
                <Checkbox
                  size="md"
                  isChecked={isSameAsMobile}
                  onChange={(e) => {
                    setIsSameAsMobile(e.target.checked);
                    setFieldValue("whatsappNumber", e.target.checked ? values.phoneNumber : "", true);
                    setTimeout(() => {
                      formik.validateField("whatsappNumber");
                    }, 0);
                  }}
                >
                  <Text fontSize="xs">Same as Mobile</Text>
                </Checkbox>
              </Flex>
              <Input
                placeholder="Enter WhatsApp Number"
                name="whatsappNumber"
                isRequired
                value={values.whatsappNumber}
                inputMode="numeric"
                onChange={handleChange}
                onBlur={handleBlur}
                isReadOnly={isSameAsMobile}
                _placeholder={{ fontSize: "sm", color: "#90A3BF" }}
                variant="outline"
                borderRadius="lg"
                bg={values.whatsappNumber ? "gray.100" : "white"}
                _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                w="100%"
                h="56px"
              />
              <FieldError error={errors.whatsappNumber} touched={touched.whatsappNumber} value={values.whatsappNumber} />
            </GridItem>

            {/* Email */}
            <GridItem>
              <LabelInput
                placeholder="Enter Vendor Email"
                label="Vendor Email"
                name="vendorEmail"
                isRequired
                value={values.vendorEmail}
                onChange={handleChange}
                onBlur={handleBlur}
                bg={values.vendorEmail ? "gray.100" : "white"}
              />
              <FieldError error={errors.vendorEmail} touched={touched.vendorEmail} value={values.vendorEmail} />
            </GridItem>

           <GridItem>
            <LabelInput
              placeholder="1,2 etc"
              label="No of Vehicles"
              value={noOfFleet}
              inputMode="numeric"
              onChange={(e) => {
                let value = e.target.value.replace(/[^0-9]/g, ""); // allow only numbers
                if (value.length > 3) value = value.slice(0, 3);   // limit to 3 digits
                setNoOfFleet(value);
              }}
            />
          </GridItem>

          </Grid>

          {/* KYC Information */}
          <Heading fontSize="lg" mb={6} color="#1A202C">KYC Information</Heading>
          <Grid templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }} gap={4} mb={6}>
            <GridItem>
              <FormLabel fontSize="1rem" color="#1A202C" htmlFor="vendorType">
                Vendor Type <Text as="span" color="red.500">*</Text>
              </FormLabel>
              <Select
                placeholder="Select Vendor Type"
                name="vendorType"
                id="vendorType"
                value={values.vendorType || ""}
                onChange={(e) => {
                  const val = e.target.value;
                  setFieldValue("vendorType", val, true);
                  setFieldTouched("vendorType", true, true);
                  setFieldValue("vendorContacts", val === "organisation" ? [{ name: "", email: "", mobile: "" }] : []);
                  setTimeout(() => {
                    formik.validateField("vendorType");
                  }, 0);
                }}
                onBlur={handleBlur}
                variant="outline"
                borderRadius="lg"
                bg={values.vendorType ? "gray.100" : "white"}
                _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                w="100%"
                h="56px"
                isInvalid={!!errors.vendorType && touched.vendorType}
              >
                <option value="individual">Individual/Sole Proprietorship</option>
                <option value="organisation">Organisation</option>
              </Select>
              <FieldError error={errors.vendorType} touched={touched.vendorType} value={values.vendorType} />
            </GridItem>
          </Grid>

          {/* ── Individual Vendor Fields ── */}
          {values.vendorType === "individual" && (
            <Box>
              <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4} mb={6}>
                <GridItem>
                  <LabelInput
                    placeholder="e.g., 123456789012"
                    label="Aadhaar Number"
                    name="aadhaarNumberIndividual"
                    type="tel"
                    isRequired
                    value={values.aadhaarNumberIndividual}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    inputMode="numeric"
                    bg={values.aadhaarNumberIndividual ? "gray.100" : "white"}
                  />
                  <FieldError error={errors.aadhaarNumberIndividual} touched={touched.aadhaarNumberIndividual} value={values.aadhaarNumberIndividual} />
                  {!errors.aadhaarNumberIndividual && values.aadhaarNumberIndividual && values.aadhaarNumberIndividual.length === 12 && (
                    <Text fontSize="xs" color="green.500" mt={1}>✓ Valid Aadhaar number</Text>
                  )}
                  {!errors.aadhaarNumberIndividual && values.aadhaarNumberIndividual && values.aadhaarNumberIndividual.length !== 12 && values.aadhaarNumberIndividual.length > 0 && (
                    <Text fontSize="xs" color="#90A3BF" mt={1}>Must be exactly 12 digits</Text>
                  )}
                </GridItem>

                <GridItem>
                  <LabelInput
                    placeholder="e.g., ABCDE1234F"
                    label="PAN Number"
                    name="panNumberIndividual"
                    isRequired
                    value={values.panNumberIndividual}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    bg={values.panNumberIndividual ? "gray.100" : "white"}
                  />
                  <FieldError error={errors.panNumberIndividual} touched={touched.panNumberIndividual} value={values.panNumberIndividual} />
                  {!errors.panNumberIndividual && values.panNumberIndividual && PAN_REGEX.test(values.panNumberIndividual) && (
                    <Text fontSize="xs" color="green.500" mt={1}>✓ Valid PAN number</Text>
                  )}
                  {!errors.panNumberIndividual && values.panNumberIndividual && !PAN_REGEX.test(values.panNumberIndividual) && values.panNumberIndividual.length > 0 && (
                    <Text fontSize="xs" color="#90A3BF" mt={1}>Format: ABCDE1234F</Text>
                  )}
                </GridItem>
              </Grid>

              <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={4} mb={6}>
                <InputAndDragAndDrop title="Upload Aadhaar Card Front" name="aadhaarCardFront" url={values.aadhaarCardFront} setData={setData} setFieldValue={setFieldValue} setFieldTouched={setFieldTouched} />
                <InputAndDragAndDrop title="Upload Aadhaar Card Back" name="aadhaarCardBack" url={values.aadhaarCardBack} setData={setData} setFieldValue={setFieldValue} setFieldTouched={setFieldTouched} />
                <InputAndDragAndDrop title="Upload PAN Card Front" name="panCardFront" url={values.panCardFront} setData={setData} setFieldValue={setFieldValue} setFieldTouched={setFieldTouched} />
              </Grid>

              {/* GST Registration — Collapsible */}
              <Box mb={6}>
                <Flex
                  alignItems="center"
                  justifyContent="space-between"
                  cursor="pointer"
                  onClick={() => setIsGstSectionOpen(!isGstSectionOpen)}
                  bg="gray.50"
                  p={4}
                  borderRadius="lg"
                  _hover={{ bg: "gray.100" }}
                >
                  <Heading fontSize="md" color="#1A202C">GST Registration</Heading>
                  <Icon as={isGstSectionOpen ? ChevronUpIcon : ChevronDownIcon} boxSize={6} color="gray.600" />
                </Flex>
                <Collapse in={isGstSectionOpen} animateOpacity>
                  <Box pt={4}>
                    <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={4} mb={4}>
                      <GridItem>
                        <LabelInput
                          placeholder="Enter Trade Name (If Different)"
                          label="Trade Name"
                          name="tradeNameIndividual"
                          value={values.tradeNameIndividual}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          bg={values.tradeNameIndividual ? "gray.100" : "white"}
                        />
                        <FieldError error={errors.tradeNameIndividual} touched={touched.tradeNameIndividual} value={values.tradeNameIndividual} />
                      </GridItem>
                      <GridItem>
                        <LabelInput
                          placeholder="e.g., 22ABCDE1234F1Z5"
                          label="GSTIN"
                          name="gstinIndividual"
                          value={values.gstinIndividual}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          bg={values.gstinIndividual ? "gray.100" : "white"}
                        />
                        <FieldError error={errors.gstinIndividual} touched={touched.gstinIndividual} value={values.gstinIndividual} />
                        {!errors.gstinIndividual && values.gstinIndividual && GSTIN_REGEX.test(values.gstinIndividual) && (
                          <Text fontSize="xs" color="green.500" mt={1}>✓ Valid GSTIN</Text>
                        )}
                        {!errors.gstinIndividual && values.gstinIndividual && !GSTIN_REGEX.test(values.gstinIndividual) && values.gstinIndividual.length > 0 && (
                          <Text fontSize="xs" color="#90A3BF" mt={1}>Format: 22ABCDE1234F1Z5 (15 characters)</Text>
                        )}
                      </GridItem>
                      <GridItem>
                        <FormLabel fontSize="1rem" color="#1A202C">GST State</FormLabel>
                        <Select
                          placeholder="Select GST State"
                          name="gstStateIndividual"
                          value={values.gstStateIndividual}
                          onChange={handleSelectChange}
                          onBlur={handleBlur}
                          variant="outline"
                          borderRadius="lg"
                          bg={values.gstStateIndividual ? "gray.100" : "white"}
                          _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                          w="100%"
                          h="56px"
                        >
                          {states.map((s) => <option key={s} value={s}>{s}</option>)}
                        </Select>
                        <FieldError error={errors.gstStateIndividual} touched={touched.gstStateIndividual} value={values.gstStateIndividual} />
                      </GridItem>
                    </Grid>
                    <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={4}>
                      <InputAndDragAndDrop title="Upload GST Certificate" name="gstCertificate" url={values.gstCertificate} setData={setData} setFieldValue={setFieldValue} setFieldTouched={setFieldTouched} />
                    </Grid>
                  </Box>
                </Collapse>
              </Box>
            </Box>
          )}

          {/* ── Organisation Vendor Fields ── */}
          {values.vendorType === "organisation" && (
            <>
              {/* Company KYC */}
              <Heading fontSize="md" mb={4} color="#1A202C">Company KYC</Heading>
              <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={4} mb={4}>
                <GridItem>
                  <LabelInput
                    placeholder="Enter company name"
                    label="Company Name"
                    name="companyName"
                    isRequired
                    value={values.companyName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    bg={values.companyName ? "gray.100" : "white"}
                  />
                  <FieldError error={errors.companyName} touched={touched.companyName} value={values.companyName} />
                </GridItem>
                <GridItem>
                  <LabelInput
                    placeholder="e.g., ABCDE1234F"
                    label="Company PAN Number"
                    name="companyPanNumber"
                    isRequired
                    value={values.companyPanNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    bg={values.companyPanNumber ? "gray.100" : "white"}
                  />
                  <FieldError error={errors.companyPanNumber} touched={touched.companyPanNumber} value={values.companyPanNumber} />
                  {!errors.companyPanNumber && values.companyPanNumber && PAN_REGEX.test(values.companyPanNumber) && (
                    <Text fontSize="xs" color="green.500" mt={1}>✓ Valid PAN number</Text>
                  )}
                  {!errors.companyPanNumber && values.companyPanNumber && !PAN_REGEX.test(values.companyPanNumber) && values.companyPanNumber.length > 0 && (
                    <Text fontSize="xs" color="#90A3BF" mt={1}>Format: ABCDE1234F</Text>
                  )}
                </GridItem>
              </Grid>
              <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={4} mb={6}>
                <InputAndDragAndDrop title="Upload Company PAN Card" name="companyPanCardFront" url={values.companyPanCardFront} setData={setData} setFieldValue={setFieldValue} setFieldTouched={setFieldTouched} />
              </Grid>

              {/* Company Address */}
              <Heading fontSize="md" mb={4} color="#1A202C">Company Address</Heading>
              <Grid templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }} gap={4} mb={6}>
                <GridItem colSpan={{ base: 1, md: 2 }}>
                  <LabelInput
                    placeholder="Enter Company Address"
                    label="Address"
                    name="organisationAddress"
                    isRequired
                    value={values.organisationAddress}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    bg={values.organisationAddress ? "gray.100" : "white"}
                  />
                  <FieldError error={errors.organisationAddress} touched={touched.organisationAddress} value={values.organisationAddress} />
                </GridItem>

                {/* City with autocomplete */}
                <GridItem>
                  <Box position="relative">
                    <FormLabel fontSize="1rem" color="#1A202C">
                      City <Text as="span" color="red.500">*</Text>
                    </FormLabel>
                    <Input
                      placeholder="Enter City"
                      name="organisationCity"
                      value={values.organisationCity || ""}
                      onChange={(e) => handleCityChange(e.target.value)}
                      onFocus={() => setShowCityDropdown(true)}
                      onBlur={() => {
                        handleBlur({ target: { name: "organisationCity" } });
                        setTimeout(() => setShowCityDropdown(false), 200);
                      }}
                      autoComplete="off"
                      bg={values.organisationCity ? "gray.100" : "white"}
                      variant="outline"
                      borderRadius="lg"
                      h="56px"
                      _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                      isInvalid={!!errors.organisationCity && touched.organisationCity}
                    />
                    {loadingCity && (
                      <Spinner size="sm" position="absolute" right="10px" top="50%" transform="translateY(-50%)" />
                    )}
                    {showCityDropdown && citySuggestions.length > 0 && (
                      <List
                        position="absolute"
                        zIndex={10}
                        bg="white"
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="lg"
                        width="100%"
                        maxHeight="240px"
                        overflowY="auto"
                        mt={1}
                        boxShadow="lg"
                      >
                        {citySuggestions.map((location, index) => (
                          <ListItem
                            key={index}
                            px={4}
                            py={2.5}
                            cursor="pointer"
                            _hover={{ bg: "blue.50" }}
                            onMouseDown={() => handleCitySelect(location)}
                          >
                            {location.description || location.name}
                          </ListItem>
                        ))}
                      </List>
                    )}
                    <FieldError error={errors.organisationCity} touched={touched.organisationCity} value={values.organisationCity} />
                  </Box>
                </GridItem>

                {/* state */}
                {/* <GridItem>
                  <FormLabel fontSize="1rem" color="#1A202C">
                    State <Text as="span" color="red.500">*</Text>
                  </FormLabel>
                  <Select
                    placeholder="Select State"
                    name="organisationState"
                    value={values.organisationState || ""}
                    onChange={handleSelectChange}
                    onBlur={handleBlur}
                    variant="outline"
                    borderRadius="lg"
                    bg={values.organisationState ? "gray.100" : "white"}
                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                    w="100%"
                    h="56px"
                    isInvalid={!!errors.organisationState && touched.organisationState}
                  >
                    {states.map((s) => <option key={s} value={s}>{s}</option>)}
                  </Select>
                  <FieldError error={errors.organisationState} touched={touched.organisationState} value={values.organisationState} />
                </GridItem> */}

                <GridItem>
                  <LabelInput
                    placeholder="Enter Pin Code"
                    label="Pin Code"
                    name="organisationPincode"
                    isRequired
                    value={values.organisationPincode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    inputMode="numeric"
                    bg={values.organisationPincode ? "gray.100" : "white"}
                  />
                  <FieldError error={errors.organisationPincode} touched={touched.organisationPincode} value={values.organisationPincode} />
                </GridItem>

                <GridItem>
                  <LabelInput
                    placeholder="e.g., India"
                    label="Country"
                    name="organisationCountry"
                    isRequired
                    value={values.organisationCountry}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    bg={values.organisationCountry ? "gray.100" : "white"}
                  />
                  <FieldError error={errors.organisationCountry} touched={touched.organisationCountry} value={values.organisationCountry} />
                </GridItem>
              </Grid>

              {/* GST Registration — Collapsible */}
              <Box mb={6}>
                <Flex
                  alignItems="center"
                  justifyContent="space-between"
                  cursor="pointer"
                  onClick={() => setIsOrgGstSectionOpen(!isOrgGstSectionOpen)}
                  bg="gray.50"
                  p={4}
                  borderRadius="lg"
                  _hover={{ bg: "gray.100" }}
                >
                  <Heading fontSize="md" color="#1A202C">GST Registration</Heading>
                  <Icon as={isOrgGstSectionOpen ? ChevronUpIcon : ChevronDownIcon} boxSize={6} color="gray.600" />
                </Flex>
                <Collapse in={isOrgGstSectionOpen} animateOpacity>
                  <Box pt={4}>
                    <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={4} mb={4}>
                      <GridItem>
                        <LabelInput
                          placeholder="Enter Trade Name (If Different)"
                          label="Trade Name"
                          name="tradeNameOrganisation"
                          value={values.tradeNameOrganisation}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          bg={values.tradeNameOrganisation ? "gray.100" : "white"}
                        />
                        <FieldError error={errors.tradeNameOrganisation} touched={touched.tradeNameOrganisation} value={values.tradeNameOrganisation} />
                      </GridItem>
                      <GridItem>
                        <LabelInput
                          placeholder="e.g., 22ABCDE1234F1Z5"
                          label="GSTIN"
                          name="gstin"
                          value={values.gstin}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          bg={values.gstin ? "gray.100" : "white"}
                        />
                        <FieldError error={errors.gstin} touched={touched.gstin} value={values.gstin} />
                        {!errors.gstin && values.gstin && GSTIN_REGEX.test(values.gstin) && (
                          <Text fontSize="xs" color="green.500" mt={1}>✓ Valid GSTIN</Text>
                        )}
                        {!errors.gstin && values.gstin && !GSTIN_REGEX.test(values.gstin) && values.gstin.length > 0 && (
                          <Text fontSize="xs" color="#90A3BF" mt={1}>Format: 22ABCDE1234F1Z5 (15 characters)</Text>
                        )}
                      </GridItem>
                      <GridItem>
                        <FormLabel fontSize="1rem" color="#1A202C">GST State</FormLabel>
                        <Select
                          placeholder="Select GST State"
                          name="gstState"
                          value={values.gstState}
                          onChange={handleSelectChange}
                          onBlur={handleBlur}
                          variant="outline"
                          borderRadius="lg"
                          bg={values.gstState ? "gray.100" : "white"}
                          _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                          w="100%"
                          h="56px"
                        >
                          {states.map((s) => <option key={s} value={s}>{s}</option>)}
                        </Select>
                        <FieldError error={errors.gstState} touched={touched.gstState} value={values.gstState} />
                      </GridItem>
                    </Grid>
                    <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={4}>
                      <InputAndDragAndDrop title="Upload GST Certificate" name="gstCertificate" url={values.gstCertificate} setData={setData} setFieldValue={setFieldValue} setFieldTouched={setFieldTouched} />
                    </Grid>
                  </Box>
                </Collapse>
              </Box>

              {/* Company Owner KYC — all fields optional */}
              <Heading fontSize="md" mb={4} color="#1A202C">Company Owner KYC</Heading>
              <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4} mb={4}>
                <GridItem>
                  <LabelInput
                    placeholder="e.g., 123456789012"
                    label="Owner Aadhaar Number"
                    name="ownerAadhaarNumber"
                    type="tel"
                    value={values.ownerAadhaarNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    inputMode="numeric"
                    bg={values.ownerAadhaarNumber ? "gray.100" : "white"}
                  />
                  <FieldError error={errors.ownerAadhaarNumber} touched={touched.ownerAadhaarNumber} value={values.ownerAadhaarNumber} />
                  {!errors.ownerAadhaarNumber && values.ownerAadhaarNumber && values.ownerAadhaarNumber.length === 12 && (
                    <Text fontSize="xs" color="green.500" mt={1}>✓ Valid Aadhaar number</Text>
                  )}
                  {!errors.ownerAadhaarNumber && values.ownerAadhaarNumber && values.ownerAadhaarNumber.length !== 12 && values.ownerAadhaarNumber.length > 0 && (
                    <Text fontSize="xs" color="#90A3BF" mt={1}>Must be exactly 12 digits</Text>
                  )}
                </GridItem>
                <GridItem>
                  <LabelInput
                    placeholder="e.g., ABCDE1234F"
                    label="Owner PAN Number"
                    name="ownerPanNumber"
                    value={values.ownerPanNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    bg={values.ownerPanNumber ? "gray.100" : "white"}
                  />
                  <FieldError error={errors.ownerPanNumber} touched={touched.ownerPanNumber} value={values.ownerPanNumber} />
                  {!errors.ownerPanNumber && values.ownerPanNumber && PAN_REGEX.test(values.ownerPanNumber) && (
                    <Text fontSize="xs" color="green.500" mt={1}>✓ Valid PAN number</Text>
                  )}
                  {!errors.ownerPanNumber && values.ownerPanNumber && !PAN_REGEX.test(values.ownerPanNumber) && values.ownerPanNumber.length > 0 && (
                    <Text fontSize="xs" color="#90A3BF" mt={1}>Format: ABCDE1234F</Text>
                  )}
                </GridItem>
              </Grid>
              <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={4} mb={6}>
                <InputAndDragAndDrop title="Upload Aadhaar Card Front" name="aadhaarCardFront" url={values.aadhaarCardFront} setData={setData} setFieldValue={setFieldValue} setFieldTouched={setFieldTouched} />
                <InputAndDragAndDrop title="Upload Aadhaar Card Back" name="aadhaarCardBack" url={values.aadhaarCardBack} setData={setData} setFieldValue={setFieldValue} setFieldTouched={setFieldTouched} />
                <InputAndDragAndDrop title="Upload Owner PAN Card" name="ownerPanCardFront" url={values.ownerPanCardFront} setData={setData} setFieldValue={setFieldValue} setFieldTouched={setFieldTouched} />
              </Grid>
            </>
          )}

          {/* ── Vendor Contacts (Organisation Only) ── */}
          {values.vendorType === "organisation" && (
            <>
              <Flex justifyContent="space-between" alignItems="center" mb={4}>
                <Heading fontSize="md" color="#1A202C">Vendor Contacts</Heading>
                <Button
                  onClick={handleAddNewContacts}
                  variant="outline"
                  colorScheme="blue"
                  borderRadius="lg"
                  leftIcon={<IoMdAdd />}
                >
                  Add Contact
                </Button>
              </Flex>
              <Box gap={4} mb={6}>
                {values.vendorContacts?.map((contact, index) => (
                  <Flex key={index} wrap="wrap" gap={4} mb={4}>
                    <Box flex={1} minW="200px">
                      <LabelInput
                        placeholder="Enter Contact Name"
                        label="Contact Name"
                        name="name"
                        isRequired
                        value={values.vendorContacts[index]?.name}
                        onChange={(e) => {
                          const v = e.target.value.replace(/[^A-Za-z\s]/g, "");
                          setFieldValue(`vendorContacts[${index}].name`, v, true);
                          setFieldTouched(`vendorContacts[${index}].name`, true, true);
                          setTimeout(() => {
                            formik.validateField(`vendorContacts[${index}].name`);
                          }, 0);
                        }}
                        bg={values.vendorContacts[index]?.name ? "gray.100" : "white"}
                      />
                      {errors.vendorContacts?.[index]?.name && touched.vendorContacts?.[index]?.name && (
                        <Text color="red.500" fontSize="xs" mt={1}>{errors.vendorContacts[index].name}</Text>
                      )}
                    </Box>
                    <Box flex={1} minW="200px">
                      <LabelInput
                        placeholder="Enter Contact Number"
                        label="Contact Number"
                        name="mobile"
                        type="tel"
                        isRequired
                        value={values.vendorContacts[index]?.mobile}
                        onChange={(e) => {
                          const v = e.target.value.replace(/\D/g, "").slice(0, 10);
                          setFieldValue(`vendorContacts[${index}].mobile`, v, true);
                          setFieldTouched(`vendorContacts[${index}].mobile`, true, true);
                          setTimeout(() => {
                            formik.validateField(`vendorContacts[${index}].mobile`);
                          }, 0);
                        }}
                        inputMode="numeric"
                        bg={values.vendorContacts[index]?.mobile ? "gray.100" : "white"}
                      />
                      {errors.vendorContacts?.[index]?.mobile && touched.vendorContacts?.[index]?.mobile && (
                        <Text color="red.500" fontSize="xs" mt={1}>{errors.vendorContacts[index].mobile}</Text>
                      )}
                    </Box>
                    <Box flex={1} minW="200px">
                      <LabelInput
                        placeholder="Enter Email"
                        label="Email"
                        name="email"
                        isRequired
                        value={values.vendorContacts[index]?.email}
                        onChange={(e) => {
                          setFieldValue(`vendorContacts[${index}].email`, e.target.value, true);
                          setFieldTouched(`vendorContacts[${index}].email`, true, true);
                          setTimeout(() => {
                            formik.validateField(`vendorContacts[${index}].email`);
                          }, 0);
                        }}
                        bg={values.vendorContacts[index]?.email ? "gray.100" : "white"}
                      />
                      {errors.vendorContacts?.[index]?.email && touched.vendorContacts?.[index]?.email && (
                        <Text color="red.500" fontSize="xs" mt={1}>{errors.vendorContacts[index].email}</Text>
                      )}
                    </Box>
                    <Button
                      onClick={() => handleRemoveContact(index)}
                      variant="ghost"
                      colorScheme="red"
                      hidden={values.vendorContacts.length <= 1}
                      borderRadius="lg"
                      h="56px"
                      mt={6}
                    >
                      <IoPersonRemove />
                    </Button>
                  </Flex>
                ))}
              </Box>
            </>
          )}
        </Box>

        {/* ── Step 2: Basic Parameters ── */}
        <Box px={6} py={8} borderRadius="lg" bg="white" my="2rem">
          <Heading fontSize="lg" mb={4} color="#1A202C">Basic Parameters</Heading>
          <Flex justifyContent="space-between" fontSize="0.875rem" letterSpacing={0} mb={6}>
            <Text color="#90A3BF">Additional vendor settings</Text>
            <Text color="#90A3BF">Step 2 of 2</Text>
          </Flex>

          <Grid templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }} gap={4} mb={6}>
            <GridItem>
              <FormLabel fontSize="1rem" color="#1A202C" htmlFor="vendorStatus">Status</FormLabel>
              <Select
                name="vendorStatus"
                id="vendorStatus"
                value={values.vendorStatus}
                onChange={handleSelectChange}
                onBlur={handleBlur}
                variant="outline"
                borderRadius="lg"
                bg={values.vendorStatus ? "gray.100" : "white"}
                _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                w="100%"
                h="56px"
              >
                <option value="under_review">Under Review</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </Select>
              <FieldError error={errors.vendorStatus} touched={touched.vendorStatus} value={values.vendorStatus} />
            </GridItem>

            <GridItem>
              <LabelInput
                placeholder="Enter Additional Details"
                label="Additional Details"
                name="additionalDetails"
                value={values.additionalDetails}
                onChange={handleChange}
                onBlur={handleBlur}
                bg={values.additionalDetails ? "gray.100" : "white"}
              />
              <FieldError error={errors.additionalDetails} touched={touched.additionalDetails} value={values.additionalDetails} />
            </GridItem>
          </Grid>

          <Flex justifyContent="flex-end" gap={4}>
            <Link to="/fleet-management/vendor">
              <Button
                type="button"
                color="#396AFF"
                variant="outline"
                border="2px solid #396AFF"
                borderRadius="md"
                px={10}
              >
                <Text color="#396AFF">Cancel</Text>
              </Button>
            </Link>
            <Button
              type="submit"
              bgColor="#396AFF"
              borderRadius="md"
              px={10}
              color="white"
              _hover={{ bgColor: "#2952CC" }}
              isLoading={formik.isSubmitting}
              loadingText="Submitting..."
            >
              Submit
            </Button>
          </Flex>
        </Box>

      </form>
    </Box>
  );
};

export default Form;